import { UploadService } from "@/services/APIs";
import {
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Button, Upload } from "antd";
import type { UploadProps, UploadFile } from "antd/es/upload";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export const CustomFileUpload = (
  props: UploadProps & {
    action?: string;
    value?: any;
    onChange?: (value: any) => void;
    folderName?: string;
  }
) => {
  useParams();
  // Destructure value and onChange to avoid passing them to antd's Upload
  const { value, onChange, folderName = "attachments", ...restProps } = props;

  const [uploadedIds, setUploadedIds] = useState<any[]>([]);
  const [resolvedFiles, setResolvedFiles] = useState<any[]>([]);

  // 1. Sync internal state with Formily value (ID list)
  useEffect(() => {
    if (!value) {
      if (uploadedIds.length > 0) {
        setUploadedIds([]);
        setResolvedFiles([]);
      }
      return;
    }

    try {
      let parsedValue = value;

      // Handle JSON string cases (from backend)
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
          try {
            parsedValue = JSON.parse(trimmed);
          } catch (_e) {
            parsedValue = [value];
          }
        } else {
          parsedValue = [value];
        }
      }

      const finalArray = Array.isArray(parsedValue)
        ? parsedValue
        : [parsedValue];

      // Store only IDs/Paths for internal tracking
      const ids = finalArray.map(item =>
        typeof item === "object" ? (item.id ?? item.file_path) : item
      );

      if (JSON.stringify(ids) !== JSON.stringify(uploadedIds)) {
        setUploadedIds(ids);
      }
    } catch (_e) {
      console.warn("CustomFileUpload: Error processing value", _e);
    }
  }, [value, uploadedIds]);

  // 2. Resolve IDs to Full File Objects for UI display
  useEffect(() => {
    const resolveFiles = async () => {
      if (!uploadedIds || uploadedIds.length === 0) {
        setResolvedFiles([]);
        return;
      }

      const results = await Promise.all(
        uploadedIds.map(async fid => {
          if (!fid) {
            return null;
          }
          // If already a URL or object with path, keep it
          if (
            typeof fid === "string" &&
            (fid.startsWith("http") || fid.includes("/"))
          ) {
            return { file_path: fid, name: fid.split("/").pop() };
          }
          if (typeof fid === "object") {
            return fid;
          }

          // Otherwise, fetch metadata from API
          try {
            const res = await UploadService.getFileById({ id: fid });
            return res;
          } catch (_e) {
            return { id: fid, name: "File not found" };
          }
        })
      );

      const filtered = results.filter(Boolean);
      if (JSON.stringify(filtered) !== JSON.stringify(resolvedFiles)) {
        setResolvedFiles(filtered);
      }
    };

    resolveFiles();
  }, [uploadedIds, resolvedFiles]);

  const handleFileUpload = async (file: File) => {
    try {
      const response = await UploadService.uploadFiles({
        folderName: folderName ?? "uploads",
        files: [file],
      });

      if (response && Array.isArray(response)) {
        const uploadedItems = response;
        // Extract IDs for Formily state
        const newIds = uploadedItems.map(item => item.id ?? item.file_path);

        let newValue;
        if (restProps.maxCount === 1) {
          newValue = newIds[0];
        } else {
          const safeValue = Array.isArray(uploadedIds) ? uploadedIds : [];
          newValue = [...safeValue, ...newIds];
        }

        onChange?.(newValue);
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
    return false; // Prevent AntD default upload behavior
  };

  const handleRemove = (file: any) => {
    const fileIdToRemove = file.id ?? file.uid;

    const filteredIds = uploadedIds.filter(fid => {
      const currentId = typeof fid === "object" ? fid.id : fid;
      return currentId !== fileIdToRemove;
    });

    onChange?.(filteredIds);
  };

  const handlePreview = (file: any) => {
    let url = file.url ?? file.file_path;
    if (!url && typeof file === "string") {
      url = file;
    }
    if (!url) {
      return;
    }

    // Force HTTPS for preview if needed
    const apiBase = (import.meta.env.VITE_API_BASE_URL ?? "").replace(
      /^https?:\/\//,
      ""
    );
    if (apiBase && url.startsWith(`http://${apiBase}`)) {
      url = url.replace("http://", "https://");
    }

    const win = window.open(url, "_blank");
    if (win) {
      win.focus();
    }
  };

  const fileList: UploadFile[] = resolvedFiles.map(
    (file: any, index: number) => {
      const originalName =
        file?.file_original_name ?? file?.name ?? "Attachment";
      const truncatedName =
        originalName.length > 25
          ? originalName.substring(0, 22) + "..."
          : originalName;

      return {
        uid: file?.id ?? `file-${index}`,
        name: truncatedName,
        status: "done",
        url: file?.file_path ?? file?.url,
      };
    }
  );

  const showUploadButton =
    !restProps.maxCount || fileList.length < restProps.maxCount;

  return (
    <div className="w-full space-y-2">
      <Upload
        {...restProps}
        onChange={undefined} // CRITICAL: Stop antd events from reaching Formily
        beforeUpload={handleFileUpload}
        onRemove={handleRemove}
        fileList={fileList}
        onPreview={handlePreview}
        showUploadList={{
          showRemoveIcon: !restProps.disabled,
          showDownloadIcon: true,
          showPreviewIcon: true,
          downloadIcon: <DownloadOutlined />,
          removeIcon: <DeleteOutlined />,
          previewIcon: <EyeOutlined />,
        }}
        className="w-full"
      >
        {showUploadButton && !restProps.disabled && (
          <Button icon={<UploadOutlined />}>Upload Attachment</Button>
        )}
      </Upload>
      <style>{`
        .ant-upload-list { width: 100% !important; }
        .ant-upload-list-item-name { 
          max-width: calc(100% - 120px) !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }
      `}</style>
    </div>
  );
};
